import { datadogRum } from '@datadog/browser-rum';
import { useVisibilityChange } from '@uidotdev/usehooks';
import { WebSocket as ReconnectingWebSocket } from 'partysocket';
import { createContext, type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAccessToken } from '../auth/use-access-token';
import type { ConnectionStatus, InboundWsAction, Send, Subscribe } from './messages';
import { INBOUND_WS_ACTIONS } from './messages';

type InternalHandler = (raw: Record<string, unknown>) => void;

const INBOUND_ACTIONS: ReadonlySet<InboundWsAction> = new Set(INBOUND_WS_ACTIONS);

const isInboundAction = (value: unknown): value is InboundWsAction =>
  typeof value === 'string' && INBOUND_ACTIONS.has(value as InboundWsAction);

const WS_URL = import.meta.env.VITE_WS_URL;

export type StoreWebSocketContextValue = {
  subscribe: Subscribe;
  send: Send;
  status: {
    subscribe: (listener: () => void) => () => void;
    getSnapshot: () => ConnectionStatus;
  };
};

export const StoreWebSocketContext = createContext<StoreWebSocketContextValue | null>(null);

export interface StoreWebSocketProviderProps {
  storeId: string;
  children: ReactNode;
}

export function StoreWebSocketProvider({
  storeId,
  children,
}: StoreWebSocketProviderProps): ReactNode {
  const getAccessTokenSilently = useAccessToken();
  const socketRef = useRef<ReconnectingWebSocket | null>(null);

  const subscribersRef = useRef(new Map<InboundWsAction, Set<InternalHandler>>());
  const statusRef = useRef<ConnectionStatus>('closed');
  const statusListenersRef = useRef(new Set<() => void>());

  const notifyStatus = useCallback((next: ConnectionStatus): void => {
    statusRef.current = next;
    for (const l of statusListenersRef.current) {
      l();
    }
  }, []);

  const subscribe = useCallback<Subscribe>((action, payloadSchema, handler) => {
    const wrapper: InternalHandler = (raw) => {
      try {
        const payload = payloadSchema.validateSync(raw, { strict: true });
        handler(payload);
      } catch (err) {
        datadogRum.addError(err, { ws_action: action, raw });
      }
    };

    let handlers = subscribersRef.current.get(action);
    if (!handlers) {
      handlers = new Set();
      subscribersRef.current.set(action, handlers);
    }

    handlers.add(wrapper);

    return () => {
      handlers?.delete(wrapper);
    };
  }, []);

  const send = useCallback<Send>((action, payloadSchema, payload) => {
    const sock = socketRef.current;
    if (!sock || sock.readyState !== ReconnectingWebSocket.OPEN) {
      console.log('[ws] send dropped (socket closed)', { action, payload });

      return 'dropped_closed';
    }

    let validated: unknown;
    try {
      validated = payloadSchema.validateSync(payload, { strict: true });
    } catch (err) {
      datadogRum.addError(err, { ws_action: action, payload });
      console.log('[ws] send dropped (invalid payload)', { action, payload, err });

      return 'dropped_invalid';
    }

    const wire = { ...(validated as object), action };
    sock.send(JSON.stringify(wire));
    console.log('[ws] sent', { action, payload: validated });

    return 'sent';
  }, []);

  const statusStore = useMemo(
    () => ({
      subscribe: (listener: () => void) => {
        statusListenersRef.current.add(listener);

        return () => {
          statusListenersRef.current.delete(listener);
        };
      },
      getSnapshot: () => statusRef.current,
    }),
    []
  );

  useEffect(() => {
    if (!WS_URL) {
      throw new Error(
        'VITE_WS_URL is not set. Add it to .env.local to enable WebSocket connections.'
      );
    }

    const buildUrl = async (): Promise<string> => {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams({
        token,
        store_id: storeId,
        client_type: 'web',
      });

      return `${WS_URL}?${params.toString()}`;
    };

    notifyStatus('connecting');
    const rws = new ReconnectingWebSocket(buildUrl, [], {
      minReconnectionDelay: 1_000,
      maxReconnectionDelay: 30_000,
      reconnectionDelayGrowFactor: 2,
    });
    socketRef.current = rws;

    let keepaliveTimer: ReturnType<typeof setInterval> | null = null;

    const startKeepalive = (): void => {
      if (keepaliveTimer !== null) {
        return;
      }

      keepaliveTimer = setInterval(
        () => {
          socketRef.current?.send(JSON.stringify({ action: 'ping' }));
        },
        5 * 60 * 1000
      );
    };

    const clearKeepalive = (): void => {
      if (keepaliveTimer !== null) {
        clearInterval(keepaliveTimer);
        keepaliveTimer = null;
      }
    };

    const handleOpen = (): void => {
      notifyStatus('open');
      // Ask the backend to reconcile our store data version: any store_updated
      // broadcast missed while this socket was down is recovered on reconnect.
      // Sent raw like the keepalive ping — both are low-level control frames
      // that bypass the validated send() wrapper.
      rws.send(JSON.stringify({ action: 'hello' }));
      startKeepalive();
    };

    const handleClose = (): void => {
      notifyStatus('closed');
      clearKeepalive();
    };

    const handleMessage = (event: MessageEvent): void => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(event.data);
      } catch {
        datadogRum.addError(new Error('websocket: JSON parse failure'), {
          ws_raw_data: typeof event.data === 'string' ? event.data.slice(0, 200) : '<non-string>',
        });

        return;
      }

      if (parsed === null || typeof parsed !== 'object' || !('action' in parsed)) {
        datadogRum.addError(new Error('websocket: missing action field'), {
          raw: parsed,
        });

        return;
      }

      const raw = parsed as Record<string, unknown>;
      const action = raw.action;
      if (!isInboundAction(action)) {
        datadogRum.addError(new Error('websocket: unknown action'), {
          ws_action: action,
          raw,
        });

        return;
      }

      const { action: _ignored, ...payload } = raw;
      console.log('[ws] received', { action, payload });
      const handlers = subscribersRef.current.get(action);
      if (!handlers) {
        return;
      }

      for (const wrapper of handlers) {
        try {
          wrapper(payload);
        } catch (err) {
          console.error('StoreWebSocketProvider: handler threw', err);
        }
      }
    };

    rws.addEventListener('open', handleOpen);
    rws.addEventListener('close', handleClose);
    rws.addEventListener('message', handleMessage);

    return () => {
      rws.removeEventListener('open', handleOpen);
      rws.removeEventListener('close', handleClose);
      rws.removeEventListener('message', handleMessage);
      clearKeepalive();
      rws.close();
      socketRef.current = null;
      notifyStatus('closed');
    };
  }, [storeId, getAccessTokenSilently, notifyStatus]);

  // When the tab returns to the foreground, nudge the socket: if it's still
  // open, ping to reset API Gateway's idle timer (and any NAT entries) before
  // the user acts; if it died while backgrounded — API Gateway's 10-minute
  // idle timeout, or a frozen-tab timer throttle that stalled the keepalive —
  // force partysocket to reconnect now instead of waiting out its backoff, so
  // the viewer lease is re-acquired in time. Skip the initial mount run: the
  // socket is still connecting then and reconnect() would just churn it.
  const documentVisible = useVisibilityChange();
  const visibilityPrimedRef = useRef(false);
  useEffect(() => {
    if (!visibilityPrimedRef.current) {
      visibilityPrimedRef.current = true;

      return;
    }

    if (!documentVisible) {
      return;
    }

    const sock = socketRef.current;
    if (!sock) {
      return;
    }

    if (sock.readyState === ReconnectingWebSocket.OPEN) {
      sock.send(JSON.stringify({ action: 'ping' }));
    } else {
      sock.reconnect();
    }
  }, [documentVisible]);

  const contextValue = useMemo<StoreWebSocketContextValue>(
    () => ({ subscribe, send, status: statusStore }),
    [subscribe, send, statusStore]
  );

  return (
    <StoreWebSocketContext.Provider value={contextValue}>{children}</StoreWebSocketContext.Provider>
  );
}
